import type {ImageTextureNode} from "#material/cycles/types"
import {getDataObjectFromImageResourceForFormatAndResolution} from "#material/types/data-object-from-image-resource"
import type {ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {
    ImageTextureExtension,
    ImageTextureInterpolation,
    ImageTextureProjection,
    LoadTextureDescriptor,
    TextureResolution,
} from "#material/nodes/texture/tex-image/types"
import type {MaterialContext} from "#material/types/context"
import type {DataObject, DataObjectWithRelated, ImageFormat, MediaType, TypedImageData} from "@cm/utils"
import {assertNever, extensionForContentType, ImageColorSpace} from "@cm/utils"
import {isTypedImageData} from "@cm/utils/typed-image-data"
import {Three as THREE, ThreeTsl as TSL} from "#material/three/exports"
import {TextureThreeNode} from "@cm/three-texture-streaming"

export const resolveTextureResolution = (textureResolution: TextureResolution): number | undefined => {
    switch (textureResolution) {
        case "500px":
            return 500
        case "1000px":
            return 1000
        case "2000px":
            return 2000
        case "original":
            return undefined
        default:
            assertNever(textureResolution)
    }
}

export function selectTextureFormatAndResolutionFromOptionsAndMediaType(
    context: Pick<MaterialContext, "logger" | "progressiveTextureLoading" | "textureResolution">,
    mediaType: MediaType,
    alphaIsImportant: boolean,
    forceOriginalResolution: boolean,
): {primary: {format: ImageFormat; resolution: TextureResolution}; lowRes?: {format: ImageFormat; resolution: TextureResolution}} {
    const logger = context.logger
    if (forceOriginalResolution || (mediaType === "image/png" && alphaIsImportant)) {
        // Image may have an alpha channel, so use _only_ the original data object!
        return {
            primary: {
                format: (() => {
                    const format = extensionForContentType(mediaType)
                    if (format === "jpg" || format === "png" || format === "tif") return format
                    logger.warn(`Failed to get supported extension for content type ${mediaType}, defaulting to png`)
                    return "png"
                })(),
                resolution: "original",
            },
        }
    }

    return {
        primary: {
            format: "jpg",
            resolution: context.textureResolution,
        },
        lowRes: context.progressiveTextureLoading && context.textureResolution !== "500px" ? {format: "jpg", resolution: "500px"} : undefined,
    }
}

export function getTextureDescriptorForImageResource(
    context: Pick<MaterialContext, "textureResolution" | "progressiveTextureLoading" | "logger">,
    resolvedResource: DataObjectWithRelated | TypedImageData,
    alphaIsImportant: boolean,
    forceOriginalResolution: boolean,
): LoadTextureDescriptor {
    const {logger} = context
    if (!isTypedImageData(resolvedResource)) {
        const {primary, lowRes} = selectTextureFormatAndResolutionFromOptionsAndMediaType(
            context,
            resolvedResource.mediaType,
            alphaIsImportant,
            forceOriginalResolution,
        )
        const primaryDataObject = getDataObjectFromImageResourceForFormatAndResolution(resolvedResource, primary.format, primary.resolution, logger)
        const lowResDataObject = lowRes
            ? getDataObjectFromImageResourceForFormatAndResolution(resolvedResource, lowRes.format, lowRes.resolution, logger)
            : undefined

        if (!primaryDataObject) throw Error(`No primary data object found for image resource (data object legacyId: ${resolvedResource.legacyId})`)

        return {
            lowRes: lowResDataObject,
            primary: primaryDataObject,
        }
    } else {
        return {
            primary: resolvedResource,
        }
    }
}

function getThreeJSColorSpaceForImageColorSpace(imageColorSpace?: ImageColorSpace | null) {
    if (imageColorSpace === ImageColorSpace.Linear) {
        return THREE.LinearSRGBColorSpace
    } else if (imageColorSpace === ImageColorSpace.Srgb) {
        return THREE.SRGBColorSpace
    }
    return undefined
}

export const cyclesExtension = (extension?: ImageTextureExtension): ImageTextureNode["parameters"]["extension"] => {
    if (!extension) {
        return undefined
    }
    switch (extension) {
        case "REPEAT":
            return "periodic"
        case "EXTEND":
            return "clamp"
        case "CLIP":
            return "black"
        case "MIRROR":
            return "mirror"
    }
}

export const fromCyclesExtension = (cyclesExtension?: ImageTextureNode["parameters"]["extension"]): ImageTextureExtension | undefined => {
    if (!cyclesExtension) {
        return undefined
    }
    switch (cyclesExtension) {
        case "periodic":
            return "REPEAT"
        case "clamp":
            return "CLIP"
        case "mirror":
            return "MIRROR"
        default:
            throw new Error(`Unknown cycles extension: ${cyclesExtension}`)
    }
}

export const cyclesInterpolation = (interpolation?: ImageTextureInterpolation): ImageTextureNode["parameters"]["interpolation"] => {
    if (!interpolation) {
        return undefined
    }
    switch (interpolation) {
        case "Linear":
            return "linear"
        case "Cubic":
            return "cubic"
        case "Closest":
            return "closest"
        case "Smart":
            return "smart"
    }
}

export const fromCyclesInterpolation = (cyclesInterpolation?: ImageTextureNode["parameters"]["interpolation"]): ImageTextureInterpolation | undefined => {
    if (!cyclesInterpolation) {
        return undefined
    }
    switch (cyclesInterpolation) {
        case "linear":
            return "Linear"
        case "cubic":
            return "Cubic"
        case "closest":
            return "Closest"
        case "smart":
            return "Smart"
    }
}

export const cyclesProjection = (projection?: ImageTextureProjection): ImageTextureNode["parameters"]["projection"] => {
    if (!projection) {
        return undefined
    }
    switch (projection) {
        case "FLAT":
            return "flat"
        case "BOX":
            return "box"
        case "SPHERE":
            return "sphere"
        case "TUBE":
            return "tube"
    }
}

export const fromCyclesProjection = (cyclesProjection?: ImageTextureNode["parameters"]["projection"]): ImageTextureProjection | undefined => {
    if (!cyclesProjection) {
        return undefined
    }
    switch (cyclesProjection) {
        case "flat":
            return "FLAT"
        case "box":
            return "BOX"
        case "sphere":
            return "SPHERE"
        case "tube":
            return "TUBE"
    }
}

export const getMinFilter = (forceFiltering?: string, interpolation?: string): THREE.MinificationTextureFilter | undefined => {
    if (forceFiltering === "nearest") return THREE.NearestFilter
    else if (forceFiltering === "linear") return THREE.LinearMipMapLinearFilter

    switch (interpolation) {
        case "Linear":
            return THREE.LinearMipMapLinearFilter
        case "Smart":
            return THREE.LinearMipMapLinearFilter
        case "Closest":
            return THREE.NearestFilter
    }
    return undefined
}

export const getMagFilter = (forceFiltering?: string, interpolation?: string): THREE.MagnificationTextureFilter | undefined => {
    if (forceFiltering === "nearest") return THREE.NearestFilter
    else if (forceFiltering === "linear") return THREE.LinearFilter

    switch (interpolation) {
        case "Linear":
            return THREE.LinearFilter
        case "Closest":
            return THREE.NearestFilter
    }
    return undefined
}

const getThreeJSColorForData = (data: TypedImageData | DataObject): THREE.ColorSpace | undefined => {
    if (!isTypedImageData(data)) return getThreeJSColorSpaceForImageColorSpace(data.imageColorSpace)
    else return getThreeJSColorSpaceForImageColorSpace(data.colorSpace === "linear" ? ImageColorSpace.Linear : ImageColorSpace.Srgb)
}

export const loadTextureFromImage = async (
    evaluator: ThreeEvaluator,
    uvNode: THREE.Node<"vec3"> | undefined,
    imageResource: DataObjectWithRelated | TypedImageData,
    interpolation: string | undefined,
    wrapping: THREE.Wrapping | undefined,
    alphaIsImportant: boolean,
    disableProgressiveLoading: boolean,
    forceOriginalResolution: boolean,
    inputColorSpace: THREE.ColorSpace | undefined,
) => {
    const {logger, isTextureCached} = evaluator.context
    const {onThreeCreatedTexture, progressiveTextureLoading, forceFiltering, onRequestRedraw, loadTexture, releaseTexture} = evaluator.context
    const {primary: primaryTexture, lowRes} = getTextureDescriptorForImageResource(
        {
            ...evaluator.context,
            progressiveTextureLoading: disableProgressiveLoading ? false : progressiveTextureLoading,
        },
        imageResource,
        alphaIsImportant,
        forceOriginalResolution,
    )

    const minFilter = getMinFilter(forceFiltering, interpolation)
    const magFilter = getMagFilter(forceFiltering, interpolation)
    if (!minFilter) logger.warn(`Warning: Unsupported minification filter ${interpolation}, using LinearMipMapLinearFilter`)
    if (!magFilter) logger.warn(`Warning: Unsupported magnification filter ${interpolation}, using LinearFilter`)

    const lowResTexture = (() => {
        if (!lowRes) return undefined
        if (!isTypedImageData(primaryTexture) && isTextureCached(primaryTexture)) return undefined
        return lowRes
    })()

    const firstTexture = lowResTexture ?? primaryTexture
    const secondTexture = firstTexture !== primaryTexture ? primaryTexture : undefined

    const firstColorSpace = getThreeJSColorForData(firstTexture) ?? inputColorSpace
    if (!firstColorSpace) logger.warn("No color space value provided, using sRGB")

    const finalMinFilter = minFilter ?? THREE.LinearMipMapLinearFilter
    const finalMagFilter = magFilter ?? THREE.LinearFilter
    const finalWrapping = wrapping ?? THREE.RepeatWrapping

    const texture = await loadTexture(firstTexture, true, firstColorSpace ?? THREE.SRGBColorSpace, finalMinFilter, finalMagFilter, finalWrapping)
    const textureNode = TSL.texture(texture, uvNode)
    onThreeCreatedTexture?.(textureNode)

    if (secondTexture) {
        const secondColorSpace = getThreeJSColorForData(secondTexture) ?? inputColorSpace
        if (!secondColorSpace) logger.warn("No color space value provided, using sRGB")
        if (firstColorSpace !== secondColorSpace)
            logger.warn(
                "Color space of low resolution texture does not match color space of high resolution texture, which may cause visible seams when the texture switches",
            )
        loadTexture(secondTexture, false, secondColorSpace ?? THREE.SRGBColorSpace, finalMinFilter, finalMagFilter, finalWrapping)
            .then((secondTexture) => {
                releaseTexture(textureNode.value)
                textureNode.value = secondTexture
                onRequestRedraw?.(this)
            })
            .catch(() => {
                logger.warn("Failed to load second texture")
            })
    }

    return {
        // use TextureThreeNode to wrap the TSL.texture to allow for gradient resolution in height-to-normal / bump nodes
        color: new TextureThreeNode(textureNode) as unknown as THREE.Node<"color">,
        alpha: textureNode.a,
    }
}

export const loadTextureFromImageTiles = async (
    evaluator: ThreeEvaluator,
    uvNode: THREE.Node<"vec3"> | undefined,
    imageResource: DataObjectWithRelated,
    _interpolation: string | undefined,
    _wrapping: THREE.Wrapping | undefined,
    _alphaIsImportant: boolean,
    _inputColorSpace: THREE.ColorSpace | undefined,
): Promise<{
    color: THREE.Node<"color">
    alpha: THREE.Node<"float">
}> => {
    if (!imageResource.imageTilesDescriptor) {
        throw Error("Image resource does not have image tiles descriptor")
    }

    const {createImageTilesLookupNode} = evaluator.context
    if (!createImageTilesLookupNode) {
        throw Error("Evaluator context does not have createImageTilesLookupNode function")
    }
    const isSrgb = imageResource.imageTilesDescriptor.tileColorSpace === "Srgb"
    const textureNode = await createImageTilesLookupNode(imageResource.imageTilesDescriptor, isSrgb, uvNode)
    return {
        // We need to cast here, because we don't want to call toColor() because some nodes like height to normal and bump require a raw texture node for finite differences
        color: textureNode as unknown as THREE.Node<"color">,
        alpha: textureNode.a,
    }
}
